import { createEnvironmentSchema, type EnvironmentDto } from '@tms/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button, Dialog, Field, Input } from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import { useCreateEnvironment, useUpdateEnvironment } from './api';

interface EnvironmentValues {
  name: string;
}

export interface EnvironmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectCode: string;
  /** Set when editing an existing environment. */
  environment?: EnvironmentDto | undefined;
}

export const EnvironmentFormDialog = ({
  open,
  onOpenChange,
  projectCode,
  environment,
}: EnvironmentFormDialogProps) => {
  const { t } = useT();
  const { errorText, applyApiError } = useErrorText();
  const isEdit = environment !== undefined;
  const create = useCreateEnvironment(projectCode);
  const update = useUpdateEnvironment(projectCode);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<EnvironmentValues>({
    resolver: zodResolver(createEnvironmentSchema),
    defaultValues: { name: '' },
  });

  useEffect(() => {
    if (!open) return;
    reset({ name: environment?.name ?? '' });
  }, [open, environment, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isEdit) {
        await update.mutateAsync({ environmentId: environment.id, input: values });
        toast.success(t('environments.updated', { name: values.name }));
      } else {
        await create.mutateAsync(values);
        toast.success(t('environments.created', { name: values.name }));
      }
      onOpenChange(false);
    } catch (error) {
      if (!applyApiError(error, setError, ['name'])) {
        toast.error(errorText(error, 'environments.saveFailed'));
      }
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('environments.editTitle') : t('environments.new')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => void onSubmit()} loading={isSubmitting}>
            {isEdit ? t('common.saveChanges') : t('environments.create')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field label={t('common.name')} error={errors.name?.message} required>
          {({ id, invalid, describedBy }) => (
            <Input
              id={id}
              invalid={invalid}
              aria-describedby={describedBy}
              autoFocus
              placeholder={t('environments.newPlaceholder')}
              {...register('name')}
            />
          )}
        </Field>

        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
};
